import { registerFeatureGateForUser } from "@shuttlerock/feature-gates";
import { data } from "react-router";
import { AuthClient, GetTokenOptions } from "./internal/AuthClient";
import { FetchUserError, TokenError } from "./internal/errors";

export type GetAccessTokenSilentlyResult =
  | { accessToken: string | undefined; error?: never }
  | { accessToken?: never; error: Error };

/**
 * Provides a safe way to get the users access token without throwing or triggering an OAuth redirect.
 *
 * `accessToken` is `undefined` when there is no session. An error will be returned in the result object if the SDK fails to retrieve the token, so the failure can be handled by the caller.
 */
export async function getAccessTokenSilently(
  options?: GetTokenOptions,
): Promise<GetAccessTokenSilentlyResult> {
  try {
    return { accessToken: await AuthClient.getAccessTokenSilently(options) };
  } catch (error) {
    return { error: TokenError(error) };
  }
}

/**
 * A loader utility to get the current user and their access token.
 *
 * @example
 * import { Route } from 'react-router';
 * import { getUser } from '@shuttlerock/auth';
 *
 * export const SomePublicRoute = <Route
 *   path="/some-public-route"
 *   element={<SomePublicPage />}
 *   loader={async ({ request }) => {
 *     const userData = await getUserData(request);
 *
 *     if (userData?.user) {
 *       // do something with the user
 *     }
 *   })
 * />
 */
export async function getUserData(_: Request) {
  // Ensure cached user details are up-to-date / not expired
  const { accessToken, error } = await getAccessTokenSilently();
  if (error instanceof FetchUserError) throw data(error.message, 500);
  // Not `error`: a session that ends leaves the last signed-in user in the client.
  if (!accessToken) return undefined;

  const user = AuthClient.getUser();
  return user ? { accessToken, user } : undefined;
}

/**
 * A loader utility that ensures the user is authenticated with our IDP.
 *
 * # This function has limited use-cases. You probably want {@link requireAuth} instead.
 *
 * This should be the first function called in the loader and be outside any `try/catch` blocks, so
 * it can short-circuit the loader and require the user to login if there is no current session.
 */
export async function requireSession(
  request: Request,
  authorizationParams?: NonNullable<
    Parameters<typeof AuthClient.loginWithRedirect>[0]
  >["authorizationParams"],
) {
  const userData = await getUserData(request);

  if (!userData) {
    const url = new URL(request.url);
    const target = `${url.pathname}${url.search}`;
    await AuthClient.loginWithRedirect({
      appState: { target },
      authorizationParams,
    });
    throw data("login_required", 401);
  }

  return userData;
}

/**
 * A loader utility that forces the user to log in again, even if they have a valid session.
 */
export async function forceLogin(
  request: Request,
  authorizationParams?: NonNullable<
    Parameters<typeof AuthClient.loginWithRedirect>[0]
  >["authorizationParams"],
) {
  const url = new URL(request.url);
  const target = `${url.pathname}${url.search}`;
  await AuthClient.loginWithRedirect({
    appState: { target, didLogin: true },
    authorizationParams: { ...authorizationParams, prompt: "login" },
  });
}

/**
 * A loader utility that ensures the user is authenticated.
 *
 * This should be the first function called in the loader and be outside any `try/catch` blocks, so
 * it can short-circuit the loader and return an error response when the user is not authenticated.
 *
 * @example
 * import { Route } from 'react-router';
 * import { requireAuth } from '@shuttlerock/auth';
 * import { AdminPage } from './AdminPage';
 *
 * export const AdminRoute = <Route
 *   path="/admin"
 *   element={<AdminPage />}
 *   loader={async ({ request }) => {
 *     await requireAuth(request);
 *
 *     try {
 *       // other loader logic (load page data etc.)
 *     } catch (error) {
 *       // handle errors
 *     }
 *   }}
 * />
 */
export async function requireAuth(request: Request) {
  const userData = await requireSession(request);

  // Capability gating is constitutively allow-all; this call registers nothing
  // and is kept only until the gates-deletion PR sweeps the remaining callsites.
  registerFeatureGateForUser(userData.user);

  return userData;
}
